import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Shield,
  TrendingUp,
  Users,
  CheckCircle2,
  BookOpen,
  BarChart3,
  Zap,
  Lock,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const Divider = () => (
  <div className="w-full h-1 bg-gradient-to-r from-transparent via-[#FF4D00] to-transparent max-w-6xl mx-auto my-16 opacity-60" />
);

const AboutPage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 800, once: true });
  }, []);

  const frameworks = [
    {
      name: "COBIT 2019",
      subtitle: "IT Governance and Management",
      icon: <Shield className="w-16 h-16 text-[#FF4D00]" />,
      description:
        "COBIT 2019 is a globally recognized framework for governance and management of enterprise IT, developed by ISACA.",
      features: [
        "5 Governance and Management Domains",
        "40 Governance and Management Objectives",
        "Structured objective-based approach",
        "Risk and value focus",
      ],
      benefits: [
        "Align IT with business objectives",
        "Improve governance and management visibility",
        "Strengthen decision-making",
        "Manage IT-related risks more effectively",
      ],
    },
    {
      name: "BCM",
      subtitle: "Business Continuity Management",
      icon: <TrendingUp className="w-16 h-16 text-[#FF4D00]" />,
      description:
        "BCM helps organizations maintain essential operations during disruption and improve readiness for recovery and resilience.",
      features: [
        "6 Assessment Phases",
        "Business impact analysis",
        "Recovery strategy evaluation",
        "Validation and testing support",
      ],
      benefits: [
        "Reduce operational disruption",
        "Strengthen resilience planning",
        "Protect reputation and service delivery",
        "Support continuity readiness",
      ],
    },
  ];

  const whyChoose = [
    {
      icon: <BarChart3 className="w-12 h-12 text-[#FF4D00]" />,
      title: "Comprehensive Assessments",
      description:
        "Structured evaluations covering key governance, management, and continuity assessment areas across COBIT 2019 and BCM.",
    },
    {
      icon: <CheckCircle2 className="w-12 h-12 text-[#FF4D00]" />,
      title: "Evidence-Based Scoring",
      description:
        "Upload supporting documents to strengthen assessment responses and support reliable review outcomes.",
    },
    {
      icon: <TrendingUp className="w-12 h-12 text-[#FF4D00]" />,
      title: "Capability Tracking",
      description:
        "Monitor capability and readiness progress over time with automated scoring and structured assessment outputs.",
    },
    {
      icon: <Users className="w-12 h-12 text-[#FF4D00]" />,
      title: "Collaborative Process",
      description:
        "Support input from multiple stakeholders across departments for broader and more accurate assessments.",
    },
    {
      icon: <BookOpen className="w-12 h-12 text-[#FF4D00]" />,
      title: "Best-Practice Alignment",
      description:
        "Built around recognized frameworks and practical assessment workflows to support consistent results.",
    },
    {
      icon: <Lock className="w-12 h-12 text-[#FF4D00]" />,
      title: "Secure and Controlled Access",
      description:
        "Protect assessment data with role-based access and secure handling of evidence and reporting outputs.",
    },
  ];

  const useCases = [
    {
      role: "IT Directors and CIOs",
      icon: "👔",
      description:
        "Assess current governance and management capability and identify strategic improvement priorities.",
    },
    {
      role: "Risk and Compliance Managers",
      icon: "📋",
      description:
        "Support governance reviews, strengthen accountability, and improve assessment evidence quality.",
    },
    {
      role: "Internal Auditors",
      icon: "🔍",
      description:
        "Conduct structured reviews and generate evidence-supported interim reports for internal evaluation.",
    },
    {
      role: "Business Continuity Teams",
      icon: "🛡️",
      description:
        "Evaluate organizational resilience and identify continuity gaps that need focused improvement.",
    },
    {
      role: "Enterprise Architects",
      icon: "🏗️",
      description:
        "Align governance and continuity practices with enterprise strategy, control needs, and transformation goals.",
    },
    {
      role: "Executives and Board Members",
      icon: "📊",
      description:
        "Access concise views of governance and business continuity assessment outcomes for oversight and decision-making.",
    },
  ];

  const stats = [
    { number: "20+", label: "Organizations Using Our Tool" },
    { number: "15+", label: "Assessments Completed" },
    { number: "98%", label: "User Satisfaction Rate" },
    { number: "2", label: "Assessment Frameworks" },
  ];

  return (
    <div className="min-h-screen bg-white text-gray-800 font-sans">
      <Helmet>
        <title>About | Governance and Business Continuity Assessment Tool</title>
        <meta
          name="description"
          content="Learn about COBIT 2019 and BCM assessment frameworks, platform capabilities, and how organizations use our governance and business continuity assessment tool."
        />
      </Helmet>

      <section className="relative py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100 overflow-hidden">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF4D00] rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-5xl mx-auto text-center relative z-10" data-aos="fade-down">
          <h1 className="text-5xl sm:text-6xl font-bold mb-6">
            About Our{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF4D00] to-orange-600">
              Assessment Platform
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Helping organizations assess, improve, and demonstrate governance and business continuity capability with confidence.
          </p>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12" data-aos="fade-up">
            <h2 className="text-4xl font-bold mb-6">Our Mission</h2>
            <p className="text-xl text-[#FF4D00] font-semibold italic mb-8">
              "Assessing governance and resilience supports stronger organizational performance"
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div data-aos="fade-right">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">What We Do</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We believe organizations should have access to practical, structured tools for assessing governance and business continuity capability. Our platform makes enterprise-style assessment workflows easier to access and use.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We help teams evaluate their current capability, identify improvement opportunities, and produce evidence-supported interim reporting aligned with recognized frameworks and internal review needs.
              </p>
            </div>

            <div data-aos="fade-left">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Our Frameworks</h3>

              <div className="mb-6 p-6 bg-blue-50 rounded-lg border-l-4 border-blue-600">
                <h4 className="text-lg font-bold text-blue-900 mb-2">🏛️ COBIT 2019</h4>
                <p className="text-blue-800 text-sm">
                  Evaluate governance and management capability across 5 governance and management domains and 40 governance and management objectives, aligned with enterprise strategy and IT priorities.
                </p>
              </div>

              <div className="p-6 bg-green-50 rounded-lg border-l-4 border-green-600">
                <h4 className="text-lg font-bold text-green-900 mb-2">🔄 Business Continuity Management</h4>
                <p className="text-green-800 text-sm">
                  Assess organizational preparedness and resilience across 6 assessment phases to strengthen continuity planning and disruption readiness.
                </p>
              </div>
            </div>
          </div>

          <div
            className="mt-12 p-8 bg-gradient-to-r from-[#FF4D00]/10 to-orange-600/10 rounded-xl border border-[#FF4D00]/20 text-center"
            data-aos="fade-up"
          >
            <h3 className="text-2xl font-bold mb-4">Ready to Start Your Assessment?</h3>
            <p className="text-gray-700 mb-6">
              Choose COBIT 2019 or BCM and understand your current governance and business continuity assessment position.
            </p>
            <button
              onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 px-8 py-3 bg-[#FF4D00] text-white rounded-lg font-semibold hover:bg-orange-700 transition"
            >
              Begin Assessment
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Assessment <span className="text-[#FF4D00]">Frameworks</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-12">
            {frameworks.map((framework, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition duration-300 border border-gray-200"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                <div className="flex items-center gap-4 mb-6">
                  {framework.icon}
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">{framework.name}</h3>
                    <p className="text-gray-600">{framework.subtitle}</p>
                  </div>
                </div>

                <p className="text-gray-700 mb-6 leading-relaxed">
                  {framework.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                  <ul className="space-y-2">
                    {framework.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-gray-700">
                        <Zap className="text-[#FF4D00] flex-shrink-0" size={18} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Business Benefits:</h4>
                  <ul className="space-y-2">
                    {framework.benefits.map((benefit, bidx) => (
                      <li key={bidx} className="flex items-center gap-2 text-gray-700">
                        <CheckCircle2 className="text-green-600 flex-shrink-0" size={18} />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Why Choose Our <span className="text-[#FF4D00]">Platform?</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChoose.map((item, idx) => (
              <div
                key={idx}
                className="p-8 bg-white rounded-xl shadow hover:shadow-lg transition duration-300 border border-gray-200"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="mb-4 p-3 bg-[#FF4D00]/10 rounded-lg w-fit">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Who <span className="text-[#FF4D00]">Uses Our Tool?</span>
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, idx) => (
              <div
                key={idx}
                className="bg-white p-8 rounded-xl shadow hover:shadow-lg hover:-translate-y-1 transition duration-300 border border-gray-200"
                data-aos="fade-up"
                data-aos-delay={idx * 80}
              >
                <div className="text-5xl mb-4">{useCase.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">
                  {useCase.role}
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {useCase.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            Trusted by Organizations Across <span className="text-orange-100">South Africa</span>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <div key={idx} className="text-center" data-aos="zoom-in" data-aos-delay={idx * 100}>
                <p className="text-5xl font-bold mb-2">{stat.number}</p>
                <p className="text-orange-100">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl font-bold text-center mb-16" data-aos="fade-up">
            How Our <span className="text-[#FF4D00]">Platform Works</span>
          </h2>

          <div className="grid md:grid-cols-4 gap-8">
            {[
              {
                step: "1",
                title: "Request Access",
                description: "Submit a request to begin your assessment.",
              },
              {
                step: "2",
                title: "Account Setup",
                description: "Our team creates your account and provides your login credentials.",
              },
              {
                step: "3",
                title: "Complete Assessment",
                description: "Answer framework questions and upload supporting evidence.",
              },
              {
                step: "4",
                title: "Review Interim Report",
                description: "Review scores, findings, recommendations, and downloadable interim reporting.",
              },
            ].map((item, idx) => (
              <div key={idx} data-aos="fade-up" data-aos-delay={idx * 100}>
                <div className="bg-gradient-to-br from-[#FF4D00] to-orange-600 text-white w-16 h-16 rounded-full flex items-center justify-center text-3xl font-bold mb-4 mx-auto">
                  {item.step}
                </div>
                <h3 className="text-lg font-bold text-center mb-2 text-gray-900">
                  {item.title}
                </h3>
                <p className="text-gray-700 text-center text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Divider />

      <section className="py-20 px-6 bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="max-w-4xl mx-auto text-center" data-aos="fade-up">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Assess Your{" "}
            <span className="text-[#FF4D00]">Governance and Business Continuity Capability?</span>
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Join organizations improving governance and continuity outcomes through structured assessment and evidence-backed insight.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate("/login")}
              className="px-8 py-4 bg-gradient-to-r from-[#FF4D00] to-orange-600 text-white rounded-lg font-semibold hover:shadow-lg transition"
            >
              Start Your Assessment
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="px-8 py-4 border-2 border-[#FF4D00] text-[#FF4D00] rounded-lg font-semibold hover:bg-[#FF4D00]/5 transition"
            >
              Request a Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;